import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Loader2, Trash2, Pencil } from 'lucide-react';
import {
  AdRule,
  AdRuleBody,
  AdRuleRun,
  createAdRule,
  deleteAdRule,
  getAdRuleRuns,
  getAdRules,
  updateAdRule,
} from '../../api/ads/ads.api';
import {
  DEFAULT_MIN_IMPRESSIONS,
  MAX_RULES_PER_CAMPAIGN,
  RULE_TEMPLATES,
  WINDOW_OPTIONS,
  actionLabel,
  budgetChangeText,
  describeRule,
  formatCa,
  isBudgetTemplate,
  isPauseTemplate,
  metricText,
  validateRule,
} from '../../utils/ads-automation';

const inputCls =
  'mt-1 w-full rounded-md ui-divider border bg-transparent px-3 py-2 text-sm outline-none ui-text';

interface Draft {
  template: string;
  windowDays: number;
  threshold: string;
  minImpressions: string;
  changePercent: string;
}
const emptyDraft: Draft = {
  template: 'PauseHighCpc',
  windowDays: 3,
  threshold: '',
  minImpressions: String(DEFAULT_MIN_IMPRESSIONS),
  changePercent: '20',
};

const toBody = (d: Draft, enabled: boolean): AdRuleBody => ({
  template: d.template,
  windowDays: d.windowDays,
  threshold: Number(d.threshold.replace(',', '.')),
  minImpressions: isPauseTemplate(d.template) ? Number(d.minImpressions) : null,
  changePercent: isBudgetTemplate(d.template) ? Number(d.changePercent) : null,
  enabled,
});

const fmtTime = (iso: string) => new Date(iso).toLocaleString('vi-VN');

const AdRulesPanel: React.FC<{ campaignId: string; hasDailyBudget: boolean; ended?: boolean }> = ({
  campaignId,
  hasDailyBudget,
  ended = false,
}) => {
  const [rules, setRules] = useState<AdRule[]>([]);
  const [runs, setRuns] = useState<AdRuleRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [editing, setEditing] = useState<AdRule | null>(null);
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);

  const load = useCallback(async () => {
    try {
      const [r, h] = await Promise.all([getAdRules(campaignId), getAdRuleRuns(campaignId, 50)]);
      setRules(r);
      setRuns(h);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Không tải được luật tự động.');
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    load();
  }, [load]);

  // Chạy một thao tác ghi: khoá bằng ref để bấm đúp không gửi hai lần.
  const guarded = async (fn: () => Promise<void>, fail: string) => {
    if (lock.current) return;
    lock.current = true;
    setBusy(true);
    try {
      await fn();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || fail);
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };

  const preview = {
    template: draft.template,
    windowDays: draft.windowDays,
    threshold: Number(draft.threshold.replace(',', '.')) || 0,
    minImpressions: isPauseTemplate(draft.template) ? Number(draft.minImpressions) : null,
    changePercent: isBudgetTemplate(draft.template) ? Number(draft.changePercent) : null,
  };

  const save = () =>
    guarded(async () => {
      const err = validateRule(preview, hasDailyBudget);
      if (err) return void toast.error(err);
      if (!editing && rules.length >= MAX_RULES_PER_CAMPAIGN)
        return void toast.error(`Mỗi chiến dịch tối đa ${MAX_RULES_PER_CAMPAIGN} luật.`);
      if (editing) {
        await updateAdRule(campaignId, editing.id, toBody(draft, editing.enabled));
        toast.success('Đã lưu luật.');
      } else {
        await createAdRule(campaignId, toBody(draft, true));
        toast.success('Đã thêm luật.');
      }
      setDraft(emptyDraft);
      setEditing(null);
      await load();
    }, 'Không lưu được luật.');

  const toggle = (r: AdRule) =>
    guarded(async () => {
      await updateAdRule(campaignId, r.id, {
        template: r.template,
        windowDays: r.windowDays,
        threshold: r.threshold,
        minImpressions: r.minImpressions,
        changePercent: r.changePercent,
        enabled: !r.enabled,
      });
      await load();
    }, 'Không đổi được trạng thái luật.');

  const remove = (r: AdRule) =>
    guarded(async () => {
      if (!window.confirm('Xoá luật này?')) return;
      await deleteAdRule(campaignId, r.id);
      if (editing?.id === r.id) {
        setEditing(null);
        setDraft(emptyDraft);
      }
      await load();
    }, 'Không xoá được luật.');

  const startEdit = (r: AdRule) => {
    setEditing(r);
    setDraft({
      template: r.template,
      windowDays: r.windowDays,
      threshold: String(r.threshold),
      minImpressions: String(r.minImpressions ?? DEFAULT_MIN_IMPRESSIONS),
      changePercent: String(r.changePercent ?? 20),
    });
  };

  if (loading)
    return (
      <div className="h-32 grid place-items-center">
        <Loader2 className="animate-spin ui-muted" />
      </div>
    );

  const budgetTpl = isBudgetTemplate(draft.template);

  return (
    <div className="space-y-4">
      <p className="text-xs ui-muted">
        Hệ thống kiểm tra chiến dịch mỗi giờ và tự điều chỉnh khi số liệu vượt ngưỡng bạn đặt. Mỗi luật chạy
        tối đa một lần trong 24 giờ, và ngân sách ngày chỉ được luật đổi tối đa một lần trong 24 giờ. Tối đa{' '}
        {MAX_RULES_PER_CAMPAIGN} luật mỗi chiến dịch.
      </p>

      <div className="space-y-2">
        {rules.length === 0 ? (
          <p className="text-sm ui-muted">Chưa có luật nào.</p>
        ) : (
          rules.map((r) => (
            <div key={r.id} className="rounded-lg ui-divider border p-3 flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className={`text-sm ui-text ${r.enabled ? '' : 'opacity-60'}`}>{describeRule(r)}</div>
                <div className="text-[11px] ui-muted mt-0.5">
                  {r.enabled ? 'Đang bật' : 'Đang tắt'}
                  {r.lastTriggeredAt ? ` · lần gần nhất ${fmtTime(r.lastTriggeredAt)}` : ' · chưa chạy lần nào'}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  disabled={busy || ended}
                  onClick={() => toggle(r)}
                  className="rounded-md ui-divider border px-2 py-1 text-xs ui-text disabled:opacity-50"
                >
                  {r.enabled ? 'Tắt' : 'Bật'}
                </button>
                <button
                  type="button"
                  disabled={busy || ended}
                  onClick={() => startEdit(r)}
                  title="Sửa luật"
                  className="p-1.5 rounded hover:bg-black/5 dark:hover:bg-white/10 disabled:opacity-50"
                >
                  <Pencil size={14} className="ui-text" />
                </button>
                <button
                  type="button"
                  disabled={busy || ended}
                  onClick={() => remove(r)}
                  title="Xoá luật"
                  className="p-1.5 rounded hover:bg-rose-50 dark:hover:bg-rose-900/20 disabled:opacity-50"
                >
                  <Trash2 size={14} className="text-rose-500" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {ended ? (
        <p className="text-sm ui-muted">Chiến dịch đã kết thúc nên không thêm, sửa hay bật tắt luật được nữa.</p>
      ) : (
      <div className="rounded-lg bg-slate-50 dark:bg-slate-800/50 p-3 space-y-3">
        <div className="text-sm font-semibold ui-text">{editing ? 'Sửa luật' : 'Thêm luật'}</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="text-xs ui-muted">
            Mẫu luật
            <select
              className={inputCls}
              value={draft.template}
              disabled={busy}
              onChange={(e) => setDraft({ ...draft, template: e.target.value })}
            >
              {RULE_TEMPLATES.map((t) => (
                <option key={t.value} value={t.value} disabled={t.isBudget && !hasDailyBudget}>
                  {t.label}
                  {t.isBudget && !hasDailyBudget ? ' (cần đặt ngân sách ngày)' : ''}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs ui-muted">
            Khung thời gian
            <select
              className={inputCls}
              value={draft.windowDays}
              disabled={busy}
              onChange={(e) => setDraft({ ...draft, windowDays: Number(e.target.value) })}
            >
              {WINDOW_OPTIONS.map((d) => (
                <option key={d} value={d}>
                  {d === 1 ? 'Hôm qua' : `${d} ngày gần nhất`}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs ui-muted">
            {draft.template === 'PauseLowCtr' || draft.template === 'ScaleUpBudget'
              ? 'Ngưỡng tỉ lệ bấm (%)'
              : 'Ngưỡng giá mỗi lượt bấm (Ca)'}
            <input
              className={inputCls}
              inputMode="decimal"
              value={draft.threshold}
              disabled={busy}
              onChange={(e) => setDraft({ ...draft, threshold: e.target.value })}
              placeholder={draft.template === 'PauseLowCtr' || draft.template === 'ScaleUpBudget' ? 'VD: 3' : 'VD: 2000'}
            />
          </label>
          {budgetTpl ? (
            <label className="text-xs ui-muted">
              Mức thay đổi ngân sách ngày (5% đến 50%)
              <input
                type="number"
                className={inputCls}
                value={draft.changePercent}
                disabled={busy}
                onChange={(e) => setDraft({ ...draft, changePercent: e.target.value })}
              />
            </label>
          ) : (
            <label className="text-xs ui-muted">
              Số lượt hiển thị tối thiểu (từ 100)
              <input
                type="number"
                className={inputCls}
                value={draft.minImpressions}
                disabled={busy}
                onChange={(e) => setDraft({ ...draft, minImpressions: e.target.value })}
              />
            </label>
          )}
        </div>
        {!hasDailyBudget && (
          <p className="text-[11px] text-amber-600 dark:text-amber-400">
            Luật tăng hoặc giảm ngân sách cần chiến dịch có ngân sách ngày. Hãy đặt ngân sách ngày ở tab
            Ngân sách và nội dung.
          </p>
        )}
        {preview.threshold > 0 && (
          <p className="text-xs ui-text italic">{describeRule(preview)}</p>
        )}
        <div className="flex gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={save}
            className="rounded-md bg-[#0071A6] text-white text-sm px-4 py-2 disabled:opacity-50"
          >
            {busy ? 'Đang lưu…' : editing ? 'Lưu luật' : 'Thêm luật'}
          </button>
          {editing && (
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setEditing(null);
                setDraft(emptyDraft);
              }}
              className="rounded-md ui-divider border text-sm px-4 py-2 ui-text"
            >
              Huỷ sửa
            </button>
          )}
        </div>
      </div>
      )}

      <div>
        <div className="text-sm font-semibold ui-text mb-2">Lịch sử luật đã chạy</div>
        {runs.length === 0 ? (
          <p className="text-sm ui-muted">Chưa có luật nào được kích hoạt.</p>
        ) : (
          <div className="space-y-2">
            {runs.map((h) => (
              <div key={h.id} className="rounded-lg ui-divider border p-2 text-xs ui-text">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
                  <span className="ui-muted">{fmtTime(h.createdAt)}</span>
                  <span className="font-semibold">{actionLabel(h.action)}</span>
                  <span className="ui-muted">{RULE_TEMPLATES.find((t) => t.value === h.template)?.label}</span>
                </div>
                <div className="ui-muted mt-0.5">
                  Số liệu lúc đó: {h.impressions.toLocaleString('vi-VN')} lượt hiển thị,{' '}
                  {h.clicks.toLocaleString('vi-VN')} lượt bấm, đã tiêu {formatCa(h.spentCa)},{' '}
                  {metricText(h.template, h.metricValue)}
                </div>
                {(h.beforeDailyBudgetCa != null || h.afterDailyBudgetCa != null) && (
                  <div className="ui-muted">
                    Ngân sách ngày: {budgetChangeText(h.beforeDailyBudgetCa, h.afterDailyBudgetCa)}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdRulesPanel;
